/**
 * Wallet Service
 * Client-side wallet generation, import, and management
 * All private keys are encrypted with vault key and stored in IndexedDB vault
 */

import { Keypair } from '@solana/web3.js';
import bs58 from 'bs58';
import { encryptData, decryptData } from '../utils/crypto-utils';

/**
 * Generate a new Solana wallet keypair
 * @returns {Keypair} Generated keypair
 */
export function generateWallet() {
  return Keypair.generate();
}

/**
 * Import wallet from base58 private key
 * @param {string} privateKeyBase58 - Base58 encoded private key
 * @returns {Keypair} Imported keypair
 */
export function importWallet(privateKeyBase58) {
  try {
    const secretKey = bs58.decode(privateKeyBase58);
    return Keypair.fromSecretKey(secretKey);
  } catch (error) {
    throw new Error(`Invalid private key format: ${error.message}`);
  }
}

/**
 * Export wallet private key as base58
 * @param {Keypair} keypair - Solana keypair
 * @returns {string} Base58 encoded private key
 */
export function exportWallet(keypair) {
  return bs58.encode(keypair.secretKey);
}

/**
 * Encrypt wallet secret key with vault key
 * @param {Keypair} keypair - Solana keypair
 * @param {CryptoKey} vaultKey - Vault encryption key (Kdata)
 * @returns {Promise<string>} Encrypted wallet data (base64)
 */
export async function encryptWallet(keypair, vaultKey) {
  // Export secret key as base58
  const secretKeyBase58 = exportWallet(keypair);
  
  // Encrypt with vault key
  const encrypted = await encryptData(secretKeyBase58, vaultKey);
  
  return encrypted;
}

/**
 * Decrypt wallet secret key from vault
 * @param {string} encryptedWallet - Encrypted wallet data (base64)
 * @param {CryptoKey} vaultKey - Vault decryption key (Kdata)
 * @returns {Promise<Keypair>} Decrypted keypair
 */
export async function decryptWallet(encryptedWallet, vaultKey) {
  // Decrypt with vault key
  const secretKeyBase58 = await decryptData(encryptedWallet, vaultKey);
  
  // Import keypair
  return importWallet(secretKeyBase58);
}

/**
 * Get wallet structure for vault storage
 * @param {Keypair} keypair - Solana keypair
 * @param {CryptoKey} vaultKey - Vault encryption key
 * @param {object} metadata - Additional metadata (isPrimary, isDefault, etc.)
 * @returns {Promise<object>} Wallet object for vault
 */
export async function createWalletEntry(keypair, vaultKey, metadata = {}) {
  const publicKey = keypair.publicKey.toString();
  const encryptedSecret = await encryptWallet(keypair, vaultKey);
  
  return {
    id: metadata.id || crypto.randomUUID(),
    publicKey,
    encryptedSecret,
    isPrimary: metadata.isPrimary || false,
    isDefault: metadata.isDefault || false,
    createdAt: metadata.createdAt || new Date().toISOString(),
  };
}

/**
 * Get primary wallet keypair from vault
 * @param {object} vault - Vault object with wallets array
 * @param {CryptoKey} vaultKey - Vault decryption key (Kdata)
 * @returns {Promise<Keypair>} Primary wallet keypair
 */
export async function getPrimaryWalletKeypair(vault, vaultKey) {
  if (!vault || !vault.wallets || vault.wallets.length === 0) {
    throw new Error("No wallets found in vault");
  }
  
  // Find primary wallet, fallback to first wallet
  const primaryWallet = vault.wallets.find(w => w.isPrimary) || vault.wallets[0];
  
  if (!primaryWallet || !primaryWallet.encryptedSecret) {
    throw new Error("Primary wallet not found or missing encrypted secret");
  }
  
  // Decrypt and return keypair
  return await decryptWallet(primaryWallet.encryptedSecret, vaultKey);
}

/**
 * Get wallet keypair by public key from vault
 * @param {object} vault - Vault object with wallets array
 * @param {CryptoKey} vaultKey - Vault decryption key (Kdata)
 * @param {string} publicKey - Wallet public key to find
 * @returns {Promise<Keypair>} Wallet keypair
 */
export async function getWalletKeypairByPublicKey(vault, vaultKey, publicKey) {
  if (!vault || !vault.wallets || vault.wallets.length === 0) {
    throw new Error("No wallets found in vault");
  }
  
  const wallet = vault.wallets.find(w => w.publicKey === publicKey);
  
  if (!wallet || !wallet.encryptedSecret) {
    throw new Error(`Wallet not found for public key: ${publicKey}`);
  }
  
  // Decrypt and return keypair
  return await decryptWallet(wallet.encryptedSecret, vaultKey);
}
